import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Badge,
  DataTable,
  Modal,
  TextField,
  Select,
  RangeSlider,
  Box,
  Divider,
  ProgressBar,
  Spinner,
  EmptyState,
} from '@shopify/polaris';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';

interface ABVariant {
  variantId: string;
  name: string;
  isControl: boolean;
  trafficPercent: number;
  config: {
    algorithm?: string;
    productCount?: number;
    displayStyle?: string;
    recommendationType?: string;
    hideRecommendations?: boolean;
    titleOverride?: string;
  };
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  uniqueVisitors: number;
}

interface ABExperiment {
  id: string;
  name: string;
  description?: string;
  status: string;
  testType: string;
  primaryMetric: string;
  controlTrafficPercent: number;
  minSampleSize: number;
  confidenceLevel: number;
  maxDurationDays?: number;
  variants: ABVariant[];
  winnerVariantId?: string;
  winnerReason?: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
}

interface VariantResult {
  variantId: string;
  variantName: string;
  isControl: boolean;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  ctr: number;
  conversionRate: number;
  aov: number;
  revenuePerVisitor: number;
}

interface ExperimentResults {
  experimentId: string;
  experimentName: string;
  status: string;
  totalImpressions: number;
  totalConversions: number;
  variantResults: VariantResult[];
  isSignificant: boolean;
  confidenceLevel: number;
  pValue: number;
  recommendedAction: string;
}

const STATUS_BADGES: Record<string, 'info' | 'success' | 'warning' | 'attention' | 'critical'> = {
  draft: 'info',
  running: 'success',
  paused: 'warning',
  completed: 'attention',
};

export default function ABTestPage() {
  const { t } = useTranslation();
  const { shop } = useShop();
  const navigate = useNavigate();
  const fetchWithAuth = useAuthenticatedFetch();

  const [experiments, setExperiments] = useState<ABExperiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newTestType, setNewTestType] = useState('algorithm');
  const [newPrimaryMetric, setNewPrimaryMetric] = useState('conversion_rate');
  const [newTrafficSplit, setNewTrafficSplit] = useState(50);
  const [newControlAlgo, setNewControlAlgo] = useState('collaborative');
  const [newTreatmentAlgo, setNewTreatmentAlgo] = useState('content_based');
  const [creating, setCreating] = useState(false);

  // Detail / results modal
  const [selectedExperiment, setSelectedExperiment] = useState<ABExperiment | null>(null);
  const [results, setResults] = useState<ExperimentResults | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const fetchExperiments = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithAuth<ABExperiment[]>('/api/abtest');
      setExperiments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(t('abtest.loadError', 'Failed to load experiments'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [shop, fetchWithAuth]);

  useEffect(() => {
    fetchExperiments();
  }, [fetchExperiments]);

  async function handleCreate() {
    setCreating(true);
    try {
      await fetchWithAuth('/api/abtest', {
        method: 'POST',
        body: {
          name: newName,
          description: newDescription,
          testType: newTestType,
          primaryMetric: newPrimaryMetric,
          controlTrafficPercent: newTrafficSplit,
          variants: [
            {
              name: 'Control',
              isControl: true,
              trafficPercent: newTrafficSplit,
              algorithm: newControlAlgo,
            },
            {
              name: 'Treatment',
              isControl: false,
              trafficPercent: 100 - newTrafficSplit,
              algorithm: newTreatmentAlgo,
            },
          ],
        },
      });
      setShowCreateModal(false);
      setNewName('');
      setNewDescription('');
      setActionMessage(t('abtest.createSuccess'));
      await fetchExperiments();
    } catch {
      setError(t('abtest.createError'));
    } finally {
      setCreating(false);
    }
  }

  async function handleAction(experimentId: string, action: 'start' | 'pause' | 'complete' | 'delete') {
    try {
      if (action === 'delete') {
        await fetchWithAuth(`/api/abtest/${experimentId}`, { method: 'DELETE' });
      } else {
        await fetchWithAuth(`/api/abtest/${experimentId}/${action}`, { method: 'POST' });
      }
      setActionMessage(t('abtest.actionSuccess', { action }));
      await fetchExperiments();
    } catch {
      setError(t('abtest.actionError', { action }));
    }
  }

  async function handleViewResults(experiment: ABExperiment) {
    setSelectedExperiment(experiment);
    setLoadingResults(true);
    setResults(null);
    try {
      const data = await fetchWithAuth<ExperimentResults>(`/api/abtest/${experiment.id}/results`);
      setResults(data);
    } catch {
      setError(t('abtest.loadResultsError'));
    } finally {
      setLoadingResults(false);
    }
  }

  function formatPercent(val: number | undefined | null): string {
    if (val == null || isNaN(val)) return '0.00%';
    return `${(val * 100).toFixed(2)}%`;
  }

  function formatCurrency(val: number | undefined | null): string {
    if (val == null || isNaN(val)) return '$0.00';
    return `$${val.toFixed(2)}`;
  }

  const tableRows = experiments.map((exp) => {
    const totalImpressions = exp.variants.reduce((s, v) => s + v.impressions, 0);
    const totalConversions = exp.variants.reduce((s, v) => s + v.conversions, 0);
    return [
      exp.name,
      <Badge key={exp.id} tone={STATUS_BADGES[exp.status] || 'info'}>{exp.status}</Badge>,
      exp.testType,
      totalImpressions.toLocaleString(),
      totalConversions.toLocaleString(),
      exp.variants.length.toString(),
      new Date(exp.createdAt).toLocaleDateString(),
      <InlineStack key={`actions-${exp.id}`} gap="200">
        <Button size="slim" onClick={() => handleViewResults(exp)}>{t('abtest.results')}</Button>
        {exp.status === 'draft' && <Button size="slim" variant="primary" onClick={() => handleAction(exp.id, 'start')}>{t('abtest.start')}</Button>}
        {exp.status === 'running' && <Button size="slim" onClick={() => handleAction(exp.id, 'pause')}>{t('abtest.pause')}</Button>}
        {(exp.status === 'running' || exp.status === 'paused') && <Button size="slim" onClick={() => handleAction(exp.id, 'complete')}>{t('abtest.complete')}</Button>}
        {(exp.status === 'draft' || exp.status === 'completed') && <Button size="slim" tone="critical" onClick={() => handleAction(exp.id, 'delete')}>{t('common.delete')}</Button>}
      </InlineStack>,
    ];
  });

  return (
    <Page
      title={t('abtest.title', 'A/B Testing')}
      subtitle={t('abtest.subtitle', 'Compare recommendation strategies and measure their impact')}
      primaryAction={{
        content: t('abtest.create', 'Create Experiment'),
        onAction: () => setShowCreateModal(true),
      }}
      backAction={{ content: t('abtest.backLabel'), onAction: () => navigate('/app') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}
        {actionMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setActionMessage(null)}>
              <p>{actionMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Overview Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">{t('abtest.totalExperiments', 'Total Experiments')}</Text>
                <Text as="p" variant="headingLg">{experiments.length}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">{t('abtest.running', 'Running')}</Text>
                <Text as="p" variant="headingLg">{experiments.filter(e => e.status === 'running').length}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">{t('abtest.completed', 'Completed')}</Text>
                <Text as="p" variant="headingLg">{experiments.filter(e => e.status === 'completed').length}</Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Experiment Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <Box padding="800">
                <BlockStack gap="400" align="center">
                  <Spinner size="large" />
                  <Text as="p" tone="subdued">{t('abtest.loading')}</Text>
                </BlockStack>
              </Box>
            ) : experiments.length === 0 ? (
              <EmptyState
                heading={t('abtest.emptyTitle', 'No experiments yet')}
                image=""
              >
                <p>{t('abtest.emptyDesc', 'Create your first A/B test to compare recommendation algorithms and measure conversion impact.')}</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                headings={[t('abtest.tableHeadings.name'), t('abtest.tableHeadings.status'), t('abtest.tableHeadings.type'), t('abtest.tableHeadings.impressions'), t('abtest.tableHeadings.conversions'), t('abtest.tableHeadings.variants'), t('abtest.tableHeadings.created'), t('abtest.tableHeadings.actions')]}
                rows={tableRows}
              />
            )}
          </Card>
        </Layout.Section>

        {/* How It Works */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">{t('abtest.howItWorks', 'How A/B Testing Works')}</Text>
              <BlockStack gap="300">
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('abtest.howStep1Title')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('abtest.howStep1Desc')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('abtest.howStep2Title')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('abtest.howStep2Desc')}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{t('abtest.howStep3Title')}</Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t('abtest.howStep3Desc')}
                    </Text>
                  </BlockStack>
                </Box>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Experiment Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t('abtest.createTitle', 'Create A/B Experiment')}
        primaryAction={{
          content: t('abtest.createButton', 'Create'),
          onAction: handleCreate,
          loading: creating,
          disabled: !newName.trim(),
        }}
        secondaryActions={[
          { content: t('common.cancel'), onAction: () => setShowCreateModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label={t('abtest.createModal.nameLabel')}
              value={newName}
              onChange={setNewName}
              placeholder={t('abtest.createModal.namePlaceholder')}
              autoComplete="off"
            />
            <TextField
              label={t('abtest.experimentDesc', 'Description (optional)')}
              value={newDescription}
              onChange={setNewDescription}
              multiline={2}
              autoComplete="off"
            />
            <Select
              label={t('abtest.createModal.testTypeLabel')}
              helpText={t('abtest.createModal.testTypeHelp')}
              options={[
                { label: t('abtest.createModal.testTypeAlgorithm'), value: 'algorithm' },
                { label: t('abtest.createModal.testTypeLayout'), value: 'widget_layout' },
                { label: t('abtest.createModal.testTypeRecommendation'), value: 'recommendation_type' },
                { label: t('abtest.createModal.testTypeProductCount'), value: 'position_count' },
              ]}
              value={newTestType}
              onChange={setNewTestType}
            />
            <Select
              label={t('abtest.createModal.primaryMetricLabel')}
              helpText={t('abtest.createModal.primaryMetricHelp')}
              options={[
                { label: t('abtest.createModal.metricCVR'), value: 'conversion_rate' },
                { label: t('abtest.createModal.metricRevenue'), value: 'revenue' },
                { label: t('abtest.createModal.metricCTR'), value: 'ctr' },
                { label: t('abtest.createModal.metricAOV'), value: 'aov' },
              ]}
              value={newPrimaryMetric}
              onChange={setNewPrimaryMetric}
            />
            <RangeSlider
              label={t('abtest.createModal.trafficSplitLabel', { value: newTrafficSplit, rest: 100 - newTrafficSplit })}
              helpText={t('abtest.createModal.trafficSplitHelp')}
              value={newTrafficSplit}
              onChange={(v) => setNewTrafficSplit(typeof v === 'number' ? v : v[0])}
              min={10}
              max={90}
              step={5}
              output
            />
            <Divider />
            <InlineStack gap="400">
              <Box minWidth="200px">
                <Select
                  label={t('abtest.createModal.controlAlgoLabel')}
                  helpText={t('abtest.createModal.controlAlgoHelp')}
                  options={[
                    { label: t('abtest.createModal.algoCollaborative'), value: 'collaborative' },
                    { label: t('abtest.createModal.algoContentBased'), value: 'content_based' },
                    { label: t('abtest.createModal.algoTrending'), value: 'trending' },
                    { label: t('abtest.createModal.algoPersonalized'), value: 'personalized' },
                    { label: t('abtest.createModal.algoFBT'), value: 'fbt' },
                  ]}
                  value={newControlAlgo}
                  onChange={setNewControlAlgo}
                />
              </Box>
              <Box minWidth="200px">
                <Select
                  label={t('abtest.createModal.treatmentAlgoLabel')}
                  helpText={t('abtest.createModal.treatmentAlgoHelp')}
                  options={[
                    { label: t('abtest.createModal.algoCollaborative'), value: 'collaborative' },
                    { label: t('abtest.createModal.algoContentBased'), value: 'content_based' },
                    { label: t('abtest.createModal.algoTrending'), value: 'trending' },
                    { label: t('abtest.createModal.algoPersonalized'), value: 'personalized' },
                    { label: t('abtest.createModal.algoFBT'), value: 'fbt' },
                  ]}
                  value={newTreatmentAlgo}
                  onChange={setNewTreatmentAlgo}
                />
              </Box>
            </InlineStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Results Modal */}
      <Modal
        open={!!selectedExperiment}
        onClose={() => { setSelectedExperiment(null); setResults(null); }}
        title={selectedExperiment ? t('abtest.resultsFor', { name: selectedExperiment.name }) : t('abtest.resultsTitle')}
        size="large"
      >
        <Modal.Section>
          {loadingResults ? (
            <Box padding="800">
              <BlockStack gap="400" align="center">
                <Spinner size="large" />
                <Text as="p" tone="subdued">{t('abtest.calculatingSignificance')}</Text>
              </BlockStack>
            </Box>
          ) : results ? (
            <BlockStack gap="500">
              {/* Significance Banner */}
              <Banner tone={results.isSignificant ? 'success' : 'info'}>
                <p>
                  {results.isSignificant
                    ? t('abtest.significantBanner', { confidence: results.confidenceLevel ?? 95, pValue: (results.pValue ?? 0).toFixed(4) })
                    : t('abtest.notSignificantBanner', { pValue: (results.pValue ?? 1).toFixed(4) })}
                </p>
                {results.recommendedAction && (
                  <p><strong>{t('abtest.recommendedAction')}</strong> {results.recommendedAction}</p>
                )}
              </Banner>

              {/* Overview */}
              <InlineStack gap="400">
                <Card>
                  <BlockStack gap="100">
                    <Text as="p" tone="subdued" variant="bodySm">{t('abtest.totalImpressions')}</Text>
                    <Text as="p" variant="headingLg">{(results.totalImpressions ?? 0).toLocaleString()}</Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="100">
                    <Text as="p" tone="subdued" variant="bodySm">{t('abtest.totalConversions')}</Text>
                    <Text as="p" variant="headingLg">{(results.totalConversions ?? 0).toLocaleString()}</Text>
                  </BlockStack>
                </Card>
              </InlineStack>

              {/* Variant Comparison */}
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text', 'text']}
                headings={[t('abtest.resultsTableHeadings.variant'), t('abtest.resultsTableHeadings.type'), t('abtest.resultsTableHeadings.impressions'), t('abtest.resultsTableHeadings.clicks'), t('abtest.resultsTableHeadings.conversions'), t('abtest.resultsTableHeadings.ctr'), t('abtest.resultsTableHeadings.cvr'), t('abtest.resultsTableHeadings.revenue')]}
                rows={(results.variantResults || []).map((v) => [
                  v.variantName || 'Unknown',
                  v.isControl ? t('abtest.control') : t('abtest.treatment'),
                  (v.impressions ?? 0).toLocaleString(),
                  (v.clicks ?? 0).toLocaleString(),
                  (v.conversions ?? 0).toLocaleString(),
                  formatPercent(v.ctr),
                  formatPercent(v.conversionRate),
                  formatCurrency(v.revenue),
                ])}
              />

              {/* Visual comparison bars */}
              {results.variantResults && results.variantResults.length >= 2 && (
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">{t('abtest.cvrComparison')}</Text>
                  {results.variantResults.map((v) => {
                    const maxCVR = Math.max(...results.variantResults.map(r => r.conversionRate));
                    const progress = maxCVR > 0 ? (v.conversionRate / maxCVR) * 100 : 0;
                    return (
                      <Box key={v.variantId}>
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              {v.variantName} {v.isControl ? t('abtest.controlSuffix') : t('abtest.treatmentSuffix')}
                            </Text>
                            <Text as="p" variant="bodySm">{formatPercent(v.conversionRate)}</Text>
                          </InlineStack>
                          <ProgressBar progress={progress} tone={v.isControl ? 'primary' : 'success'} size="small" />
                        </BlockStack>
                      </Box>
                    );
                  })}
                </BlockStack>
              )}

              {/* Winner declaration */}
              {selectedExperiment?.winnerVariantId && (
                <Banner tone="success">
                  <p><strong>{t('abtest.winner')}</strong> {selectedExperiment.variants.find(v => v.variantId === selectedExperiment.winnerVariantId)?.name || 'Unknown'}</p>
                  {selectedExperiment.winnerReason && <p>{selectedExperiment.winnerReason}</p>}
                </Banner>
              )}
            </BlockStack>
          ) : (
            <Text as="p" tone="subdued">{t('abtest.noResultsYet')}</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
